import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useIdentity } from "@/lib/identity/useIdentity";
import { identifyLeadFromAuth } from "@/lib/lead-identify-auth.functions";

/**
 * Makes direct account signups / sign-ins show up in Admin → Leads.
 *
 * Runs once per signed-in user per browser session: on mount (already signed
 * in) and on SIGNED_IN / USER_UPDATED transitions.
 */
export function LeadAuthSync() {
  const { anonymousId, refresh } = useIdentity();
  const syncedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!anonymousId) return;
    let cancelled = false;

    const sync = async (userId: string | undefined) => {
      if (!userId || cancelled) return;
      if (syncedFor.current === userId) return;
      syncedFor.current = userId;
      try {
        const res = await identifyLeadFromAuth({ data: { anonymousId } });
        if (res?.ok && !cancelled) refresh();
      } catch {
        syncedFor.current = null;
      }
    };

    void supabase.auth.getUser().then(({ data }) => sync(data.user?.id));

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT") {
        syncedFor.current = null;
        return;
      }
      if (event !== "SIGNED_IN" && event !== "USER_UPDATED") return;
      void sync(session?.user?.id);
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anonymousId]);

  return null;
}
